import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Image as ImageIcon,
  Payments as PaymentsIcon,
  Schedule as ScheduleIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { createConsignorPayment, fetchConsignorSales } from '../api/consignorSales';
import type { ConsignorSaleItem, PayoutChannel, SalePaymentStatus } from '../types/consignorSales';
import { PAYOUT_CHANNEL_LABELS } from '../types/consignorSales';

// ─── Filter type ──────────────────────────────────────────────────────────────

import type { SalesFilter } from '../api/consignorSales';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(iso));

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<SalePaymentStatus, { label: string; bg: string; color: string }> = {
  pending: { label: 'Pendiente', bg: '#FFF7ED', color: '#B45309' },
  scheduling: { label: 'Fecha solicitada', bg: '#F5F3FF', color: '#6D28D9' },
  scheduled: { label: 'Fecha asignada', bg: '#EFF6FF', color: '#1D4ED8' },
  paid: { label: 'Cobrado', bg: '#F0FDF4', color: '#15803D' },
};

function SaleStatusChip({ status }: { status: SalePaymentStatus }): React.ReactElement {
  const { label, bg, color } = STATUS_CONFIG[status];
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.04em',
        bgcolor: bg,
        color,
        border: '1px solid',
        borderColor: `${color}33`,
      }}
    />
  );
}

// ─── Sale card ────────────────────────────────────────────────────────────────

const IN_BATCH_STATUSES: SalePaymentStatus[] = ['scheduling', 'scheduled'];

interface SaleCardProps {
  item: ConsignorSaleItem;
  /** True when there is an open payment batch and this item is part of it */
  inActiveBatch: boolean;
}

function SaleCard({ item, inActiveBatch }: SaleCardProps): React.ReactElement {
  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.75,
        mb: 1.25,
        border: '1px solid',
        borderColor: inActiveBatch ? 'rgba(251,191,36,0.5)' : 'rgba(226,232,240,1)',
        borderLeft: inActiveBatch ? '3px solid #F59E0B' : '1px solid rgba(226,232,240,1)',
        borderRadius: 2,
        bgcolor: inActiveBatch ? '#FFFDF5' : 'white',
      }}
    >
      {/* Image */}
      <Box
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'rgba(248,250,252,1)',
          border: '1px solid rgba(226,232,240,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.imageUrl ? (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.productName}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
          />
        ) : (
          <ImageIcon sx={{ color: 'action.disabled', fontSize: 24 }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {item.productName || 'Sin título'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {item.variantName || '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
          <SaleStatusChip status={item.paymentStatus} />
          {item.paymentStatus === 'scheduled' && item.paymentScheduledDate && (
            <Typography variant="caption" sx={{ color: '#1D4ED8', fontWeight: 600 }}>
              {fmtDate(item.paymentScheduledDate)}
            </Typography>
          )}
          {inActiveBatch && (
            <Typography
              variant="caption"
              sx={{
                color: '#92400E',
                fontWeight: 700,
                fontSize: 10,
                bgcolor: 'rgba(251,191,36,0.2)',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
              }}
            >
              En este pago
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          Vendido {fmtDate(item.soldAt)}
        </Typography>
      </Box>

      {/* Amount */}
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="subtitle2" fontWeight={800}>
          {moneyMx(item.payoutAmount)}
        </Typography>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ textTransform: 'uppercase', fontSize: 9 }}
        >
          MXN
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Payment hero card (no pending payment) ───────────────────────────────────

interface RequestPaymentCardProps {
  amount: number;
  onRequest: () => void;
}

function RequestPaymentCard({ amount, onRequest }: RequestPaymentCardProps): React.ReactElement {
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: '1px solid rgba(226,232,240,1)',
        borderRadius: 3,
        background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
        color: 'white',
      }}
    >
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, fontWeight: 700 }}
      >
        Saldo disponible
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5, mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>
          {moneyMx(amount)}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          MXN
        </Typography>
      </Box>
      <Button
        fullWidth
        variant="contained"
        startIcon={<PaymentsIcon />}
        onClick={onRequest}
        sx={{
          bgcolor: 'white',
          color: '#111827',
          fontWeight: 700,
          borderRadius: 2,
          py: 1.5,
          textTransform: 'none',
          fontSize: 15,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
        }}
      >
        Solicitar pago
      </Button>
    </Card>
  );
}

// ─── Payment hero card (pending payment exists) ───────────────────────────────

interface PendingPaymentCardProps {
  amount: number;
  channel: PayoutChannel;
  scheduledDate: string | null;
}

function PendingPaymentCard({
  amount,
  channel,
  scheduledDate,
}: PendingPaymentCardProps): React.ReactElement {
  return (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        border: '1px solid rgba(251,191,36,0.4)',
        borderRadius: 3,
        bgcolor: '#FFFBEB',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <ScheduleIcon sx={{ color: '#B45309' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#92400E' }}>
          Pago en proceso
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Método de pago
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {PAYOUT_CHANNEL_LABELS[channel]}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            Monto
          </Typography>
          <Typography variant="body2" fontWeight={800}>
            {moneyMx(amount)}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {scheduledDate ? (
          <>
            <CalendarIcon sx={{ fontSize: 16, color: '#1D4ED8' }} />
            <Typography variant="caption" fontWeight={600} sx={{ color: '#1D4ED8' }}>
              Programado para {fmtDate(scheduledDate)}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            El equipo de Urban Utility asignará una fecha pronto.
          </Typography>
        )}
      </Box>
    </Card>
  );
}

// ─── Payment channel picker ───────────────────────────────────────────────────

const CHANNEL_OPTIONS: { value: PayoutChannel; icon: React.ReactElement; desc: string }[] = [
  {
    value: 'cash',
    icon: <PaymentsIcon />,
    desc: 'Recibe en efectivo en tienda',
  },
  {
    value: 'transfer',
    icon: <CreditCardIcon />,
    desc: 'Transferencia a tu cuenta bancaria',
  },
  {
    value: 'store_credit',
    icon: <StoreIcon />,
    desc: 'Crédito para usar en Urban Utility',
  },
];

interface ChannelPickerDialogProps {
  open: boolean;
  creating: boolean;
  selected: PayoutChannel | null;
  onSelect: (c: PayoutChannel) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

function ChannelPickerDialog({
  open,
  creating,
  selected,
  onSelect,
  onCancel,
  onConfirm,
}: ChannelPickerDialogProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>¿Cómo quieres cobrar?</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {CHANNEL_OPTIONS.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => onSelect(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: isSelected ? '#111827' : 'rgba(226,232,240,1)',
                  bgcolor: isSelected ? '#F8FAFC' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: isSelected ? '#111827' : 'rgba(226,232,240,1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isSelected ? 'white' : 'text.secondary',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {PAYOUT_CHANNEL_LABELS[opt.value]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.desc}
                  </Typography>
                </Box>
                {isSelected && <CheckCircleIcon sx={{ ml: 'auto', color: '#111827' }} />}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={creating}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={creating || selected == null}
          variant="contained"
          startIcon={creating ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#111827',
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {creating ? 'Creando…' : 'Solicitar pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const FILTER_OPTIONS: { value: SalesFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'scheduling', label: 'Fecha solicitada' },
  { value: 'scheduled', label: 'Con fecha' },
  { value: 'paid', label: 'Cobrados' },
];

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ConsignorSalesPage(): React.ReactElement {
  const [items, setItems] = useState<ConsignorSaleItem[]>([]);
  const [summary, setSummary] = useState<{
    pendingAmount: number;
    hasPendingPayment: boolean;
    pendingPayment: {
      id: number;
      amount: number;
      channel: PayoutChannel;
      scheduledDate: string | null;
    } | null;
  }>({
    pendingAmount: 0,
    hasPendingPayment: false,
    pendingPayment: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<SalesFilter>('all');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<PayoutChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchConsignorSales(activeFilter);
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      return;
    }
    setItems(result.data.items);
    setSummary(result.data.summary);
  }, [activeFilter]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleCreatePayment = useCallback(async () => {
    if (!selectedChannel) return;
    setCreating(true);
    const result = await createConsignorPayment(selectedChannel);
    setCreating(false);
    setPickerOpen(false);
    setSelectedChannel(null);
    if (!result.ok) {
      setSnackMessage(`Error: ${result.error}`);
      return;
    }
    setSnackMessage('¡Pago solicitado! El equipo de Urban Utility asignará una fecha pronto.');
    load().catch(() => {});
  }, [selectedChannel, load]);

  const showRequestButton = !summary.hasPendingPayment && summary.pendingAmount > 0;

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, pt: 3, pb: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
          Mis ventas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Historial y pagos de tus consignaciones
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {/* Hero card */}
      {!loading && !loadError && (
        <>
          {showRequestButton && (
            <RequestPaymentCard
              amount={summary.pendingAmount}
              onRequest={() => setPickerOpen(true)}
            />
          )}
          {summary.hasPendingPayment && summary.pendingPayment && (
            <PendingPaymentCard
              amount={summary.pendingPayment.amount}
              channel={summary.pendingPayment.channel}
              scheduledDate={summary.pendingPayment.scheduledDate}
            />
          )}
          {!showRequestButton && !summary.hasPendingPayment && items.length > 0 && (
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                border: '1px solid rgba(187,247,208,0.6)',
                borderRadius: 3,
                bgcolor: '#F0FDF4',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CheckCircleIcon sx={{ color: '#15803D', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#15803D' }}>
                  Todo al día
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No tienes ventas pendientes de cobro.
                </Typography>
              </Box>
            </Card>
          )}
        </>
      )}

      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, overflowX: 'auto', pb: 0.5 }}>
        {FILTER_OPTIONS.map((f) => {
          const active = activeFilter === f.value;
          return (
            <Chip
              key={f.value}
              label={f.label}
              onClick={() => setActiveFilter(f.value)}
              size="small"
              sx={{
                fontWeight: active ? 700 : 500,
                bgcolor: active ? '#111827' : 'white',
                color: active ? 'white' : 'text.secondary',
                border: '1px solid',
                borderColor: active ? '#111827' : 'rgba(226,232,240,1)',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { bgcolor: active ? '#374151' : 'rgba(0,0,0,0.04)' },
              }}
            />
          );
        })}
      </Box>

      {/* Sales list */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: '#111827' }} />
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <PaymentsIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={600}>
            Sin ventas
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            {activeFilter !== 'all'
              ? 'No hay ventas con este estado.'
              : 'Aún no tienes artículos vendidos.'}
          </Typography>
        </Box>
      )}

      {!loading &&
        items.length > 0 &&
        items.map((item) => (
          <SaleCard
            key={item.id}
            item={item}
            inActiveBatch={
              summary.hasPendingPayment && IN_BATCH_STATUSES.includes(item.paymentStatus)
            }
          />
        ))}

      {/* Channel picker dialog */}
      <ChannelPickerDialog
        open={pickerOpen}
        creating={creating}
        selected={selectedChannel}
        onSelect={setSelectedChannel}
        onCancel={() => {
          setPickerOpen(false);
          setSelectedChannel(null);
        }}
        onConfirm={() => {
          handleCreatePayment().catch(() => {});
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackMessage != null}
        autoHideDuration={5000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
